import React, { useState, useEffect } from "react";
import {
  Layers,
  Code2,
  Smartphone,
  Monitor,
  Laptop,
  Palette,
  Sparkles,
  Zap,
  Layout,
  CheckCircle2,
  Compass,
} from "lucide-react";

/**
 * CinematicWebdesignBackdrop
 * 3D Cinematic, slightly translucent dynamic Webdesign-themed background
 * for the RedWORK homepage (https://redwork.ch).
 *
 * Features:
 * - 3D floating responsive browser viewport with live wireframe & PageSpeed 99+
 * - Floating 3D design studio canvas with Figma-style vector bezier anchors & color swatches
 * - Live dynamic code syntax tokens (<WebDesign />, CSS3 Grid, 120 FPS UI)
 * - Translucent glassmorphism (opacity: 12-20%, zero interference with foreground readability)
 * - 100% pointer-events-none click-through safe
 */
const RESOLUTIONS = [
  { label: "Desktop 4K", res: "3840 × 2160" },
  { label: "MacBook Pro", res: "1920 × 1080" },
  { label: "iPad Pro Retina", res: "1024 × 1366" },
  { label: "iPhone 16 Pro", res: "393 × 852" },
];

const CODE_TOKENS = [
  "<WebDesign modern={true} />",
  "const ui = useSwissPrecision()",
  "display: grid; columns: 12;",
  "backdrop-filter: blur(24px);",
  "Figma High-Fidelity Prototype",
  "PageSpeed: 99/100 Mobile & Web",
];

export default function CinematicWebdesignBackdrop() {
  const [activeRes, setActiveRes] = useState("1920 × 1080");
  const [pageSpeed, setPageSpeed] = useState(99);
  const [activeTokenIdx, setActiveTokenIdx] = useState(0);

  useEffect(() => {
    const resInterval = setInterval(() => {
      setActiveRes((prev) => {
        const nextIdx = (RESOLUTIONS.findIndex((r) => r.res === prev) + 1) % RESOLUTIONS.length;
        return RESOLUTIONS[nextIdx].res;
      });
      setPageSpeed((prev) => (prev === 99 ? 100 : 99));
      setActiveTokenIdx((prev) => (prev + 1) % CODE_TOKENS.length);
    }, 2800);

    return () => clearInterval(resInterval);
  }, []);

  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none select-none overflow-hidden z-10"
    >
      {/* Ambient Gradient Flares for Webdesign Atmosphere */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-tr from-[#1E88E5]/15 via-[#FF7A00]/10 to-transparent rounded-full blur-3xl" />
      <div className="absolute top-1/3 right-1/4 w-[30rem] h-[30rem] bg-gradient-to-bl from-[#E63946]/15 via-[#FFC107]/10 to-transparent rounded-full blur-3xl" />

      {/* ================================================== */}
      {/* 1. LEFT: 3D FLOATING BROWSER & WIREFRAME VIEWPORT  */}
      {/* ================================================== */}
      <div
        className="hidden lg:block absolute left-2 xl:left-8 top-28 xl:top-32 w-72 xl:w-80 pointer-events-none opacity-20 hover:opacity-35 transition-all duration-700"
        style={{
          perspective: "1200px",
        }}
      >
        <div
          className="rounded-3xl p-4 bg-[#0A0E18]/60 backdrop-blur-md border border-white/10 shadow-[0_25px_60px_rgba(0,0,0,0.7)] font-mono"
          style={{
            transform: "rotateY(18deg) rotateX(8deg) rotateZ(-2deg)",
            transformStyle: "preserve-3d",
          }}
        >
          {/* Browser Top Window Chrome */}
          <div className="flex items-center justify-between pb-2.5 mb-3 border-b border-white/10">
            <div className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-red-500/80" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-500/80" />
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/80" />
            </div>
            <div className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-[9px] text-slate-300">
              <span className="text-emerald-400 font-bold">https://</span>
              <span>redwork.ch/design</span>
            </div>
          </div>

          {/* Wireframe Mockup Layout in Browser */}
          <div className="space-y-2 p-2 rounded-2xl bg-black/40 border border-white/5 mb-3">
            {/* Header bar wireframe */}
            <div className="h-3 w-full rounded bg-white/10 flex items-center justify-between px-1.5">
              <span className="h-1.5 w-6 rounded bg-[#1E88E5]/70" />
              <div className="flex gap-1">
                <span className="h-1 w-3 rounded bg-white/20" />
                <span className="h-1 w-3 rounded bg-white/20" />
                <span className="h-1 w-3 rounded bg-white/20" />
              </div>
            </div>

            {/* Hero Banner wireframe with CTA */}
            <div className="h-14 w-full rounded-xl bg-gradient-to-r from-[#1E88E5]/20 via-[#E63946]/15 to-transparent p-2 flex flex-col justify-between border border-white/5">
              <div className="space-y-1">
                <span className="h-2 w-20 rounded bg-white/40 block" />
                <span className="h-1.5 w-28 rounded bg-white/20 block" />
              </div>
              <div className="flex items-center justify-between">
                <span className="h-2.5 w-10 rounded bg-[#FFC107] block" />
                <span className="text-[8px] font-mono text-emerald-400 font-bold">120 FPS</span>
              </div>
            </div>

            {/* 3 Column Cards wireframe */}
            <div className="grid grid-cols-3 gap-1.5 pt-0.5">
              <div className="h-10 rounded-lg bg-white/5 border border-white/5 p-1 flex flex-col justify-between">
                <span className="h-1.5 w-4 rounded bg-[#FF7A00]/80" />
                <span className="h-1 w-6 rounded bg-white/20" />
              </div>
              <div className="h-10 rounded-lg bg-white/5 border border-white/5 p-1 flex flex-col justify-between">
                <span className="h-1.5 w-4 rounded bg-[#1E88E5]/80" />
                <span className="h-1 w-6 rounded bg-white/20" />
              </div>
              <div className="h-10 rounded-lg bg-white/5 border border-white/5 p-1 flex flex-col justify-between">
                <span className="h-1.5 w-4 rounded bg-emerald-500/80" />
                <span className="h-1 w-6 rounded bg-white/20" />
              </div>
            </div>
          </div>

          {/* Live Dynamic Resolution & Speed Telemetry */}
          <div className="flex items-center justify-between text-[9px] pt-1 border-t border-white/10 text-slate-300">
            <div className="flex items-center gap-1 text-cyan-400">
              <Monitor size={11} />
              <span className="font-bold">{activeRes}</span>
            </div>
            <div className="flex items-center gap-1 text-emerald-400 font-bold">
              <Zap size={11} className="animate-pulse" />
              <span>Score: {pageSpeed}/100</span>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================== */}
      {/* 2. RIGHT: 3D FLOATING FIGMA DESIGN STUDIO CANVAS   */}
      {/* ================================================== */}
      <div
        className="hidden lg:block absolute right-2 xl:right-8 top-36 xl:top-40 w-72 xl:w-80 pointer-events-none opacity-20 hover:opacity-35 transition-all duration-700"
        style={{
          perspective: "1200px",
        }}
      >
        <div
          className="rounded-3xl p-4 bg-[#0A0E18]/60 backdrop-blur-md border border-white/10 shadow-[0_25px_60px_rgba(0,0,0,0.7)] font-mono"
          style={{
            transform: "rotateY(-18deg) rotateX(8deg) rotateZ(2deg)",
            transformStyle: "preserve-3d",
          }}
        >
          {/* Header with Pen Tool Vector Node */}
          <div className="flex items-center justify-between pb-2.5 mb-3 border-b border-white/10 text-[10px]">
            <div className="flex items-center gap-1.5 text-[#FFC107]">
              <Compass size={12} className="animate-spin" style={{ animationDuration: "20s" }} />
              <span className="font-bold tracking-wider">UI/UX DESIGN STUDIO</span>
            </div>
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#1E88E5]/20 text-[#1E88E5] font-bold border border-[#1E88E5]/30">
              FIGMA / VECTOR
            </span>
          </div>

          {/* Color Palette Swatches */}
          <div className="p-2.5 rounded-2xl bg-black/40 border border-white/5 space-y-2 mb-3">
            <span className="text-[9px] uppercase tracking-wider text-slate-400 block font-sans font-bold">
              Schweizer Farb-Architektur
            </span>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-6 rounded-lg bg-[#E63946] border border-white/20 shadow-inner flex items-center justify-center text-[8px] font-bold text-white">
                #E63946
              </div>
              <div className="flex-1 h-6 rounded-lg bg-[#FFC107] border border-white/20 shadow-inner flex items-center justify-center text-[8px] font-bold text-slate-900">
                #FFC107
              </div>
              <div className="flex-1 h-6 rounded-lg bg-[#1E88E5] border border-white/20 shadow-inner flex items-center justify-center text-[8px] font-bold text-white">
                #1E88E5
              </div>
              <div className="flex-1 h-6 rounded-lg bg-[#00F5D4] border border-white/20 shadow-inner flex items-center justify-center text-[8px] font-bold text-slate-900">
                #00F5D4
              </div>
            </div>

            {/* Bezier Vector Pen Path Simulation */}
            <div className="relative h-10 w-full rounded-xl bg-white/[0.02] border border-white/5 flex items-center justify-center overflow-hidden">
              <svg className="w-full h-full" viewBox="0 0 200 40">
                <path
                  d="M 10 20 C 60 5, 140 35, 190 20"
                  fill="transparent"
                  stroke="rgba(255, 193, 7, 0.4)"
                  strokeWidth="2"
                  strokeDasharray="4 2"
                />
                <circle cx="10" cy="20" r="3" fill="#E63946" />
                <circle cx="100" cy="20" r="3.5" fill="#FFC107" />
                <circle cx="190" cy="20" r="3" fill="#1E88E5" />
              </svg>
              <span className="absolute bottom-1 right-2 text-[8px] text-slate-400">Bezier Curve P1-P4</span>
            </div>
          </div>

          {/* Dynamic Code Token Banner */}
          <div className="p-2 rounded-xl bg-white/[0.04] border border-white/5 flex items-center justify-between text-[9px]">
            <span className="flex items-center gap-1.5 text-slate-300 font-bold truncate">
              <Code2 size={11} className="text-[#1E88E5] shrink-0" />
              <span className="truncate text-amber-300">{CODE_TOKENS[activeTokenIdx]}</span>
            </span>
            <span className="text-emerald-400 font-bold shrink-0 ml-1">CLEAN</span>
          </div>
        </div>
      </div>

      {/* ================================================== */}
      {/* 3. MOBILE: SLEEK COMPACT DESIGN HUD CHIPS          */}
      {/* ================================================== */}
      <div className="block lg:hidden absolute top-20 inset-x-3 pointer-events-none opacity-25">
        <div className="mx-auto max-w-sm p-2 rounded-2xl bg-[#0A0E18]/60 backdrop-blur-md border border-white/10 font-mono text-[9px] flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-1.5 text-[#FFC107]">
            <Layout size={11} />
            <span className="font-bold">WEBDESIGN 3.0</span>
          </div>
          <div className="text-slate-300">
            {activeRes}
          </div>
          <div className="text-emerald-400 font-bold flex items-center gap-1">
            <Zap size={10} />
            <span>99/100 SPEED</span>
          </div>
        </div>
      </div>
    </div>
  );
}
